/**
 * Images section of the Pass Designer. Renders ONE ImageSlotUploader per slot
 * that slotsForStyle(design.style) permits (the shared styleSlots map, the same
 * per-style predicates validatePassDesign enforces). For eventTicket it disables
 * the strip-XOR-(background|thumbnail) combinations so the operator can never
 * assemble a design the server would 422 on PUT. Images are TEMPLATE chrome (not
 * live data); each slot binds a single s3KeyBase.
 */

import type { DeepPartial, PassDesign } from '@insurwiz/wallet-core';

import { HelpText } from './HelpHint';
import { ImageSlotUploader } from './ImageSlotUploader';
import { eventTicketSlotDisabled, slotsForStyle, type ImageSlot } from './styleSlots';

export interface ImagesEditorProps {
  design: PassDesign;
  onChange: (patch: DeepPartial<PassDesign>) => void;
}

// Operator-facing note: the "Csík" display name (not the raw "strip" slot key)
// and no raw Apple style name ("eventTicket") leaked to the screen.
const STRIP_XOR_NOTE = 'A csík nem kombinálható háttér- vagy bélyegképpel ezen a stíluson.';

export function ImagesEditor({ design, onChange }: ImagesEditorProps): JSX.Element {
  const slots = slotsForStyle(design.style);
  const present = {
    strip: design.images.strip !== undefined,
    background: design.images.background !== undefined,
    thumbnail: design.images.thumbnail !== undefined,
  };

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-sm font-semibold text-slate-800">Képek</h3>
      <HelpText>
        Tölts fel @1x / @2x / @3x PNG-t. Elég csak az @2x — a hiányzó méreteket a rendszer pótolja.
      </HelpText>
      <div className="grid grid-cols-2 gap-3">
        {slots.map((slot: ImageSlot) => {
          const disabled = design.style === 'eventTicket' && eventTicketSlotDisabled(slot, present);
          const slotImage = design.images[slot];
          return (
            <ImageSlotUploader
              key={slot}
              slot={slot}
              s3KeyBase={slotImage?.s3KeyBase}
              disabled={disabled}
              disabledReason={STRIP_XOR_NOTE}
              onChange={onChange}
            />
          );
        })}
      </div>
    </section>
  );
}
